
import { Component, Vue, Watch, Provide, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SkeletonLoader from "@/components/SkeletonLoader.vue"

import CaseFilePreview from "@/components/CaseDetails/CaseFilePreview.vue"
import MediaPreview from "@/components/CaseDetails/Files/MediaPreview.vue"
import CaseSideBar from "@/components/CaseDetails/CaseSideBar.vue"

import LayoutHost from "@/components/CaseDetails/Layout/LayoutHost.vue"
import Desktop from "@/components/CaseDetails/Layout/Desktop.vue"
import Mobile from "@/components/CaseDetails/Layout/Mobile.vue"

import CaseFile from "@/types/caseFile"
import User from "@/types/user"

import { CaseField, CaseDetails as CaseDetailsType } from "@/types"
import { LayoutLocations } from "@/types/templateLayout"
import { SectionDetails, TemplateDetails } from "@/types/templateDetails"
import { CaseEvents } from "@/types/originatingEvents"

import { MapProvider } from "@/services/mapProvider"

import api from "@/services/api"
import FileService from "@/services/file-service"

import { CaseError } from "@/types/caseError"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import { CadenceInstance } from "@/types/cadence"
import { TenantFeatures } from "@/types/tenantFeatures"
import { cloneDeep } from "lodash"
const caseStore = namespace("cases")

@Component({
	components: {
		"page": PageWrapper,
		"scrollbar": VuePerfectScrollbar,
		"file-preview": CaseFilePreview,
		"media-preview": MediaPreview,
		"case-side-bar": CaseSideBar,
		"skeleton": SkeletonLoader,
		"layout-host": LayoutHost,
		"desktop": Desktop,
		"mobile": Mobile
	}
})
export default class CaseDetails extends Vue {
	@Prop({ type: Boolean, default: false })
	public restrictedMode!: Boolean;

	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider()
	@Provide("fileService") fileService: FileService = new FileService(this.caseFilesChanged)

	@Getter("getMe") public me!: User;
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getCurrentCase") public currentCase: any;
	@Getter("getScreenWidth") public screenWidth!: number;

	@caseStore.State public caseEvents!: CaseEvents | null;
	@caseStore.State public caseErrors!: CaseError[];
	@caseStore.State public currentTemplate!: TemplateDetails | null
	@caseStore.Mutation private setCaseToast!: (message: string | null) => void

	@caseStore.Getter("getCurrentCaseFiles")
	public caseFiles!: CaseFile[];

	@caseStore.Action caseClear!: () => void;
	@caseStore.Mutation setCurrentCase: any;
	@caseStore.Mutation setCurrentCaseNew: any;
	@caseStore.Mutation setCurrentCaseId!: (caseId: string) => void;

	@caseStore.Mutation updateCurrentCase!: (updated: CaseDetailsType) => void;
	@caseStore.Mutation updateCurrentCaseUsers!: (viewingUsers: User[]) => void;
	@caseStore.Mutation setCaseEvents!: (caseEvents: CaseEvents) => void

	@caseStore.Mutation
	public setCurrentCaseTemplate!: (template: TemplateDetails | null) => void;

	@caseStore.Mutation
	public setCurrentTemplate!: (template: TemplateDetails | null) => void;

	@caseStore.Mutation public setCurrentCaseSectionDetails!: (sectionDetails: { [key: string]: SectionDetails }) => void;
	@caseStore.Mutation("setHiddenFields") public setHiddenFields!: (hiddenFields: string[]) => void;
	@caseStore.Mutation("setCadenceDetails") public setCadenceDetails!: (cadenceDetails: any) => void;

	@caseStore.Mutation
	public setShowCCEvent!: (ccEvent: any) => void;

	@caseStore.Action public caseOpen!: (caseId: string) => Promise<void>;
	@caseStore.Action public caseRefresh!: () => Promise<void>;
	@caseStore.Action public detailsLoad!: () => Promise<void>;
	@caseStore.Action public refreshCategories!: () => Promise<void>;
	@caseStore.Action public caseCreate!: (caseDetails: CaseDetailsType) => Promise<any>;
	@caseStore.Action public caseFilesLoad!: () => void;
	@caseStore.Action public caseAuditLoad!: () => void

	@caseStore.Mutation
	cascadeListChange!: (args: { caseField: CaseField, newValue: any, getFields: boolean }) => void

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter("getTenantSettings") tenantSettings!: { [ key: string]: any }
	@Action("loadTenantSettings") loadTenantSettings!: () => Promise<void>

	private immediatelyPromoteToCase: boolean = false

	private caseLoadFailed: boolean = false;
	private caseLoadFailMessage: string = "";
	private comment: string = "";

	private template: any = null;

	private creating: boolean = false;
	private createdCaseNumber: string | null = null;

	private creatingCase: boolean = false;
	private canCreateCase: boolean = false;
	private canCreateRecord: boolean = false;
	private layoutLocations = LayoutLocations;

	private valid: boolean = false

	public keyMedia: any[] = []

	currentCaseCadenceInstance: CadenceInstance | null = null

	public get paramCaseNumber (): any {
		return this.$route && this.$route.params ? this.$route.params.caseNumber : null
	}

	public get params () {
		return this.$route.params
	}

	public get caseId (): number | null {
		return this.currentCase.caseNumber
	}

	public get createPromoted (): boolean {
		return !!this.tenantSettings && !!this.tenantSettings.buttonCreatePromoted
	}

	@Watch("paramCaseNumber")
	public async onCaseIdParamChanged (caseId: string) {
		this.caseClear()

		this.template = null

		try {
			if (caseId && caseId.toLowerCase() === "new") {
				await this.detailsLoad()

				this.setCurrentCaseNew()
				this.fileService.caseId = null
			} else if (caseId) {
				await this.caseOpen(caseId)
				this.fileService.caseId = this.currentCase.id

				this.caseFilesLoad()
			}
		} catch (error) {
			this.caseLoadFailed = true
			if (error === "You do not have permission") {
				this.caseLoadFailMessage = "You do not have permission to view this " + this.getCaseName + "."
			} else {
				this.caseLoadFailMessage = "Unable to load requested " + this.getRecordName + "."
			}
		}

		await this.fetchCadenceInstance()
	}

	public async backToList () {
		this.$router.push({ path: "/cases" })
	}

	public async mounted () {
		await this.refreshCategories()
		await this.onCaseIdParamChanged(this.$route.params.caseNumber)
		await this.fetchCadenceInstance()
	}

	public beforeDestroy () {
		this.caseClear()
	}

	public caseFilesChanged () {
		if (this.creating) {
			this.creating = false
			this.$router.replace({ name: "case", params: { caseNumber: this.createdCaseNumber! } })
		} else {
			this.caseFilesLoad()
		}
	}

	public async createNew (isCase: boolean) {
		if (this.creating) return

		this.creatingCase = isCase
		this.creating = true

		try {
			this.currentCase.isCase = isCase
			const { created, errors }: { created: CaseDetailsType, errors: CaseError[] } = await this.caseCreate(this.currentCase)

			if (errors) {
				errors.forEach(e => {
					this.$bvToast.toast(`There was a problem adding the field ${e.field.title}: ${e.message}`, {
						title: `Error adding field ${e.field.title}`,
						variant: "danger",
						solid: true,
						autoHideDelay: 10000000
					})
				})
			}

			if (this.fileService.uploadFiles.length === 0) {
				this.setCurrentCaseId(created.id!)
				this.$router.replace({ name: "case", params: { caseNumber: created.caseNumber! } }) // /${}
				this.creating = false
			} else {
				this.createdCaseNumber = created.caseNumber
				this.fileService.uploadPendingFiles(created.id!)

				this.setCurrentCaseId(created.id!)
			}
		} catch (error) {
			console.log(error)
			this.creating = false
		}
	}

	private onCaseChanged (): void {
		if (this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	private async onTemplateChange (template: TemplateDetails | null) {
		if (!this.currentCase.id && !!template) {
			this.setCurrentCaseTemplate(template)
		}

		this.setCurrentTemplate(template)

		await this.loadTenantSettings()
		this.immediatelyPromoteToCase = false
	}

	private async permissionsChanged ({ canCreateCase, canCreateRecord }: { canCreateCase: boolean, canCreateRecord: boolean }) {
		this.canCreateCase = canCreateCase
		this.canCreateRecord = canCreateRecord
	}

	private cancel () {
		this.$router.push({ path: "/cases" })
	}

	public makeCase (): void {
		this.currentCase.isCase = true
	}

	private caseNoLongerAvailable () {
		const toastMesage = this.currentCase
			? `#${this.currentCase.caseNumber} is no longer available. You may not have access to it, or it may have been removed.`
			: null

		this.caseClear()
		this.$router.push("/cases")

		if (toastMesage) {
			this.setCaseToast(toastMesage)
		}
	}

	private caseClosed () {
		this.caseClear()
		this.$router.push("/cases")
	}

	private caseUpdated (updated: CaseDetailsType) {
		const existingFields = cloneDeep(this.currentCase.fields)
		this.updateCurrentCase(updated)
		this.updateCadenceRunningTotalsIfRequired(updated.fields, existingFields)
		this.caseAuditLoad()
	}

	/**
	 * Re-fetches the cadence instance if the changed fields include/involve a running total section field
	 * in order to get any updated running total values
	 **/
	private updateCadenceRunningTotalsIfRequired (updatedFields: { [fieldId: string]: CaseField }, existingFields: { [fieldId: string]: CaseField }): void {
		if (!this.currentTemplate?.cadenceDetails?.runningTotalSectionFields) {
			return
		}

		const cadenceTotalDataTypes = ["number", "calculated", "user-role"]

		// we currently only care about the value itself or the totalTime property in the case of a user-role field
		const modifiedFields = Object.keys(updatedFields).filter(fieldId =>
			cadenceTotalDataTypes.includes(updatedFields[fieldId]!.field!.dataType!) && (
				updatedFields[fieldId].value !== existingFields[fieldId].value ||
				updatedFields[fieldId].value?.totalTime !== existingFields[fieldId].value?.totalTime
			)
		).map(fieldId => updatedFields[fieldId])

		const runningTotalFields = Object.values(this.currentTemplate!.cadenceDetails.runningTotalSectionFields)
			.flat().distinct()

		for (const modifiedField of modifiedFields) {
			if (modifiedField.field?.id && runningTotalFields.includes(modifiedField.field.id)) {
				this.fetchCadenceInstance()
				return
			}

			const dependentCalculatedFields = this.currentTemplate?.fields.filter(field =>
				field.field.dataType === "calculated" && field.field.extensions?.fields &&
				field.field.extensions.fields.includes(modifiedField.field!.id!)) ?? []

			if (dependentCalculatedFields.length > 0) {
				// If there are any calculated fields depending on the changed field, simply re-fetch the
				// running totals as it could be a chain of calculated fields that lead to a running total field
				this.fetchCadenceInstance()
				return
			}

			const dependentDataItemFields = modifiedField.field?.extensions?.dataListField
				? this.currentTemplate?.fields.filter(field =>
					(field.field.dataType === "number" || field.field.dataType === "calculated") &&
					field.field.extensions?.dataListField &&
					field.field.extensions.dataListField === modifiedField.field!.extensions!.dataListField) ?? []
				: []

			if (dependentDataItemFields.length > 0) {
				// If there are any fields dependent on the same data items as the changed field, simply re-fetch
				// the running totals as it could be a chain of fields that lead to a running total field
				this.fetchCadenceInstance()
				return
			}
		}
	}

	private get alwaysPromoteToCase (): boolean {
		return !!this.currentTemplate && !!this.currentTemplate.alwaysPromoted
	}

	private cadenceRunningTotalsFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceRunningTotals)
	}

	async fetchCadenceInstance () {
		if (!this.cadenceRunningTotalsFeatureIsEnabled) {
			return
		}

		if (this.currentCase && this.currentCase.id && this.currentCase.cadenceNumber) {
			const cadenceInstance = await api.cadenceInstance(this.currentCase.cadenceNumber)
			Vue.set(this, "currentCaseCadenceInstance", cadenceInstance)
		} else {
			Vue.set(this, "currentCaseCadenceInstance", null)
		}
	}
}
